import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import { Search, ExpandMore } from '@mui/icons-material';
import { colors } from '../../theme/theme';

interface PermissionPickerProps {
  permissions: any[];
  selected: number[];
  onChange: (selected: number[]) => void;
  rolePermissions?: number[]; // Permissions inherited from role (shown as green)
}

// Map permission prefix to friendly group name
const groupLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  modules: 'Modules',
  services: 'Services',
  charges: 'Charges',
  penalties: 'Penalties',
  discounts: 'Discounts',
  categories: 'Categories',
  schedules: 'Schedules',
  parts: 'Parts',
  applications: 'Service Applications',
  transactions: 'Transactions',
  customers: 'Customers',
  finance: 'Finance & Budget',
  reports: 'Reports',
  users: 'Users',
  roles: 'Roles',
  permissions: 'Permissions',
  profile: 'County Profile',
  locations: 'Locations',
  payment_config: 'Payment Configurations',
  approvals: 'Approvals',
  notifications: 'Notifications',
  module_templates: 'Module Templates',
  // Superadmin groups
  sa: 'System Administration',
};

const getGroupKey = (permName: string): string => {
  // For new format: users.view → users
  if (permName.includes('.')) {
    const prefix = permName.split('.')[0];
    // For sa.* permissions, group by sa.prefix
    if (prefix === 'sa') {
      const parts = permName.split('.');
      return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : prefix;
    }
    return prefix;
  }
  // For old format: manage_county_users → county_users
  const parts = permName.split('_');
  if (parts[0] === 'manage' || parts[0] === 'view' || parts[0] === 'edit' || parts[0] === 'export') {
    return parts.slice(1).join('_');
  }
  return parts.slice(0, 2).join('_');
};

const getGroupLabel = (key: string): string => {
  // Check sa.* groups
  if (key.startsWith('sa.')) {
    const subKey = key.replace('sa.', '');
    return `SA: ${groupLabels[subKey] || subKey.replace(/_/g, ' ')}`;
  }
  return groupLabels[key] || key.replace(/_/g, ' ');
};

const getPermLabel = (permName: string): string => {
  // users.view → View, users.create → Create
  if (permName.includes('.')) {
    const action = permName.split('.').pop() || '';
    return action.replace(/_/g, ' ');
  }
  // manage_county_users → Manage County Users
  return permName.replace(/_/g, ' ');
};

export const PermissionPicker = ({ permissions, selected, onChange, rolePermissions = [] }: PermissionPickerProps) => {
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group permissions by feature
  const grouped = useMemo(() => {
    const groups: Record<string, any[]> = {};
    permissions.forEach((p: any) => {
      const key = getGroupKey(p.name);
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    // Sort groups alphabetically, sort permissions within each group
    const sorted: Record<string, any[]> = {};
    Object.keys(groups).sort((a, b) => getGroupLabel(a).localeCompare(getGroupLabel(b))).forEach(key => {
      sorted[key] = groups[key].sort((a: any, b: any) => a.name.localeCompare(b.name));
    });
    return sorted;
  }, [permissions]);

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    const result: Record<string, any[]> = {};
    Object.entries(grouped).forEach(([key, perms]) => {
      const label = getGroupLabel(key).toLowerCase();
      const matchingPerms = perms.filter((p: any) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        label.includes(q)
      );
      if (matchingPerms.length > 0) result[key] = matchingPerms;
    });
    return result;
  }, [grouped, search]);

  const togglePermission = (permId: number) => {
    onChange(
      selected.includes(permId)
        ? selected.filter(id => id !== permId)
        : [...selected, permId]
    );
  };

  const toggleGroup = (groupPerms: any[]) => {
    const groupIds = groupPerms.map((p: any) => p.id);
    const allSelected = groupIds.every(id => selected.includes(id) || rolePermissions.includes(id));
    if (allSelected) {
      onChange(selected.filter(id => !groupIds.includes(id)));
    } else {
      const newSelected = Array.from(new Set([...selected, ...groupIds]));
      onChange(newSelected);
    }
  };

  const toggleAccordion = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => onChange(permissions.map((p: any) => p.id));
  const clearAll = () => onChange([]);

  const totalSelected = selected.length + rolePermissions.filter(id => !selected.includes(id)).length;

  return (
    <Box>
      {/* Search + Actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth size="small"
          placeholder="Search permissions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> } }}
        />
        <Button size="small" onClick={selectAll} sx={{ whiteSpace: 'nowrap' }}>Select All</Button>
        <Button size="small" onClick={clearAll} sx={{ whiteSpace: 'nowrap' }}>Clear</Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {totalSelected} of {permissions.length} permissions selected
      </Typography>

      {/* Grouped Permissions */}
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: `1px solid ${colors.grey[200]}`, borderRadius: 1 }}>
        {Object.entries(filteredGroups).length > 0 ? (
          Object.entries(filteredGroups).map(([key, perms]) => {
            const groupIds = perms.map((p: any) => p.id);
            const selectedInGroup = groupIds.filter((id: number) => selected.includes(id) || rolePermissions.includes(id)).length;
            const allInGroupSelected = selectedInGroup === groupIds.length;
            const someInGroupSelected = selectedInGroup > 0 && !allInGroupSelected;
            const isExpanded = expandedGroups.has(key) || search.trim().length > 0;

            return (
              <Accordion key={key} expanded={isExpanded} onChange={() => toggleAccordion(key)}
                disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${colors.grey[100]}` }}>
                <AccordionSummary expandIcon={<ExpandMore />}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}>
                  <Checkbox
                    size="small"
                    checked={allInGroupSelected}
                    indeterminate={someInGroupSelected}
                    onClick={(e) => { e.stopPropagation(); toggleGroup(perms); }}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                    {getGroupLabel(key)}
                  </Typography>
                  <Chip label={`${selectedInGroup}/${groupIds.length}`} size="small"
                    color={allInGroupSelected ? 'success' : someInGroupSelected ? 'warning' : 'default'}
                    sx={{ fontSize: '0.7rem', height: 20 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1, pl: 4 }}>
                  {perms.map((perm: any) => {
                    const fromRole = rolePermissions.includes(perm.id);
                    const directlySelected = selected.includes(perm.id);
                    const isChecked = fromRole || directlySelected;

                    return (
                      <FormControlLabel key={perm.id}
                        control={
                          <Checkbox size="small" checked={isChecked}
                            onChange={() => togglePermission(perm.id)}
                            sx={fromRole && !directlySelected ? { color: colors.success.main, '&.Mui-checked': { color: colors.success.main } } : { p: 0.5 }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                              {getPermLabel(perm.name)}
                            </Typography>
                            {fromRole && !directlySelected && (
                              <Chip label="from role" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: `${colors.success.main}15`, color: colors.success.main }} />
                            )}
                          </Box>
                        }
                        sx={{ display: 'flex', mb: 0, ml: 0 }}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {search ? 'No permissions match your search' : 'No permissions available'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
